import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getOrderBoard, setOrderPosition, updateOrderStatus, type OrderBoardCard, type OrderBoardColumnKey, type OrderBoardResponse } from '../../../shared/api/orders.js'
import type { Location } from '../../../shared/types/location.js'
import { ApiError } from '../../../shared/types/api.js'
import { canManageOrders } from '../../../shared/utils/permissions.js'
import { availableOrderActions, type OrderStatus } from '../../../shared/utils/orders.js'
import { canDropOrderToColumn, dropStatusForColumn, KANBAN_COLUMNS } from '../../../shared/utils/order-board.js'
import { ROUTES } from '../../../app/routes.js'
import OrderStatusBadge from './OrderStatusBadge.js'

type LoadStatus = 'loading' | 'ready' | 'error' | 'forbidden'

interface Props {
  organizationId: string
  role: string | undefined
  locations: Location[]
  locationFilter: string
  typeFilter: string
  onLocationChange: (value: string) => void
  onTypeChange: (value: string) => void
}

const TYPE_LABEL: Record<string, string> = {
  DELIVERY: 'orderType.delivery',
  PICKUP: 'orderType.pickup',
  DINE_IN: 'orderType.dineIn',
}

function formatDateTime(value: string, timezone: string): string {
  try {
    return new Date(value).toLocaleString(undefined, { timeZone: timezone })
  } catch {
    return new Date(value).toLocaleString()
  }
}

export default function OrdersKanban({
  organizationId,
  role,
  locations,
  locationFilter,
  typeFilter,
  onLocationChange,
  onTypeChange,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canManage = canManageOrders(role)

  const [board, setBoard] = useState<OrderBoardResponse | null>(null)
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [actionError, setActionError] = useState('')
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const pendingRef = useRef<Set<string>>(new Set())
  const draggingRef = useRef<string | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const dragSourceRef = useRef<{ id: string; column: OrderBoardColumnKey } | null>(null)
  const reorderRef = useRef(false)
  const [dropTarget, setDropTarget] = useState<{ column: OrderBoardColumnKey; beforeId: string | null } | null>(null)
  const [dragOverColumn, setDragOverColumn] = useState<OrderBoardColumnKey | null>(null)

  const load = useCallback(async () => {
    setStatus('loading')
    setActionError('')
    try {
      const data = await getOrderBoard(organizationId, {
        locationId: locationFilter || undefined,
        type: typeFilter || undefined,
      })
      setBoard(data)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error')
    }
  }, [organizationId, locationFilter, typeFilter])

  useEffect(() => {
    void load()
  }, [load])

  const cardsById = useMemo(() => {
    const map = new Map<string, OrderBoardCard>()
    for (const column of Object.values(board?.columns ?? {})) {
      for (const card of column) map.set(card.id, card)
    }
    return map
  }, [board])

  async function changeStatus(card: OrderBoardCard, nextStatus: OrderStatus) {
    if (!canManage || pendingRef.current.has(card.id) || reorderRef.current) return
    if (nextStatus === 'REJECTED' || nextStatus === 'CANCELLED') {
      const ok = window.confirm(t('orders.confirmDestructive', { status: t(`orderStatus.${nextStatus}`) }))
      if (!ok) return
    }

    setActionError('')
    pendingRef.current.add(card.id)
    setPendingIds(prev => new Set(prev).add(card.id))
    try {
      await updateOrderStatus(organizationId, card.id, { status: nextStatus })
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        // Reload first, then surface the conflict so the refresh (which clears
        // transient action errors) cannot swallow the operator-visible message.
        await load()
        setActionError(t('orders.concurrencyError'))
      } else {
        setActionError(t('orders.statusUpdateError'))
      }
      return
    } finally {
      pendingRef.current.delete(card.id)
      setPendingIds(prev => {
        const next = new Set(prev)
        next.delete(card.id)
        return next
      })
    }

    // The status was persisted; a failed refresh must not be reported as a
    // failed status change.
    await load()
  }

  function isReorderable(card: OrderBoardCard): boolean {
    return canManage && card.status !== 'COMPLETED'
  }

  function canDrag(card: OrderBoardCard): boolean {
    if (!canManage) return false
    return isReorderable(card) || KANBAN_COLUMNS.some(column => canDropOrderToColumn(card.status, column.key))
  }

  function handleDragStart(event: React.DragEvent<HTMLElement>, card: OrderBoardCard, column: OrderBoardColumnKey) {
    const target = event.target as HTMLElement
    if (target.closest('select, button, a') || !canDrag(card)) {
      event.preventDefault()
      return
    }
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', card.id)
    draggingRef.current = card.id
    dragSourceRef.current = { id: card.id, column }
    setDraggingId(card.id)
  }

  function clearDragState() {
    draggingRef.current = null
    dragSourceRef.current = null
    setDraggingId(null)
    setDragOverColumn(null)
    setDropTarget(null)
  }

  function handleDragOver(event: React.DragEvent<HTMLElement>, column: OrderBoardColumnKey) {
    const src = dragSourceRef.current
    const card = src ? cardsById.get(src.id) : undefined
    if (!card || !src) return

    if (src.column === column) {
      if (!isReorderable(card)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      const cards = board?.columns[column] ?? []
      const cardEl = (event.target as HTMLElement).closest('[data-card-id]')
      const anchorId = cardEl?.getAttribute('data-card-id') ?? null
      let beforeId: string | null = null
      if (anchorId) {
        const idx = cards.findIndex(item => item.id === anchorId)
        if (idx !== -1) {
          const rect = cardEl!.getBoundingClientRect()
          if (anchorId === src.id || !(event.clientY > rect.top + rect.height / 2)) {
            beforeId = anchorId
          } else {
            beforeId = cards[idx + 1]?.id ?? null
          }
        }
      }
      setDropTarget(prev => (prev && prev.column === column && prev.beforeId === beforeId ? prev : { column, beforeId }))
      return
    }

    if (!canDropOrderToColumn(card.status, column)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    if (dragOverColumn !== column) setDragOverColumn(column)
  }

  function handleDrop(event: React.DragEvent<HTMLElement>, column: OrderBoardColumnKey) {
    event.preventDefault()
    const src = dragSourceRef.current
    const targetBefore = dropTarget && dropTarget.column === column ? dropTarget.beforeId : undefined
    clearDragState()

    if (src && src.column === column) {
      if (targetBefore !== undefined && targetBefore !== src.id) {
        void reorderWithinColumn(src.id, column, targetBefore)
      }
      return
    }

    const id = src?.id ?? event.dataTransfer.getData('text/plain')
    const card = id ? cardsById.get(id) : undefined
    if (!card || pendingRef.current.has(card.id) || reorderRef.current) return
    if (!canDropOrderToColumn(card.status, column)) {
      setActionError(t('orders.kanban.invalidTransition'))
      return
    }
    const nextStatus = dropStatusForColumn(column)
    if (nextStatus) void changeStatus(card, nextStatus)
  }

  async function reorderWithinColumn(
    movedId: string,
    column: OrderBoardColumnKey,
    beforeId: string | null,
  ) {
    if (reorderRef.current || !board) return
    const current = board.columns[column] ?? []
    const withoutSelf = current.filter(item => item.id !== movedId)
    const currentIndex = current.findIndex(item => item.id === movedId)
    const newIndex = beforeId
      ? withoutSelf.findIndex(item => item.id === beforeId)
      : withoutSelf.length
    if (currentIndex === -1 || newIndex === -1 || newIndex === currentIndex) return

    let position: { beforeOrderId?: string; afterOrderId?: string }
    if (beforeId) {
      position = { beforeOrderId: beforeId }
    } else {
      const lastOther = withoutSelf[withoutSelf.length - 1]
      if (!lastOther) return
      position = { afterOrderId: lastOther.id }
    }

    const snapshot = board
    reorderRef.current = true
    setActionError('')
    setBoard(prev => {
      if (!prev) return prev
      const moved = (prev.columns[column] ?? []).find(item => item.id === movedId)
      if (!moved) return prev
      const list = (prev.columns[column] ?? []).filter(item => item.id !== movedId)
      const rawAt = beforeId ? list.findIndex(item => item.id === beforeId) : list.length
      list.splice(rawAt === -1 ? list.length : rawAt, 0, moved)
      return { ...prev, columns: { ...prev.columns, [column]: list } }
    })

    try {
      await setOrderPosition(organizationId, movedId, position)
    } catch {
      setBoard(snapshot)
      setActionError(t('orders.kanban.reorderError'))
      return
    } finally {
      reorderRef.current = false
    }

    // Server is the source of truth for the final ordering.
    await load()
  }

  if (status === 'loading' && !board) {
    return (
      <div className="kanban-loading" aria-label={t('orders.loading')}>
        {KANBAN_COLUMNS.map(column => (
          <div key={column.key} className="kanban-column kanban-column--skeleton">
            <div className="kanban-column-header">{t(`orders.kanban.columns.${column.key}`)}</div>
            <div className="skeleton-order-row" />
            <div className="skeleton-order-row" />
          </div>
        ))}
      </div>
    )
  }

  if (status === 'error' || status === 'forbidden') {
    return (
      <div className="settings-card">
        <div className="form-error-banner">
          {status === 'forbidden' ? t('orders.forbidden') : t('orders.loadErrorList')}
        </div>
        <button type="button" className="btn btn-primary" onClick={() => void load()}>
          {t('error.retry')}
        </button>
      </div>
    )
  }

  if (!board) return null

  return (
    <div className="kanban-wrapper">
      <div className="product-filters kanban-filters">
        <div className="form-field">
          <label htmlFor="kanbanLocationFilter">{t('orders.locationFilter')}</label>
          <select
            id="kanbanLocationFilter"
            value={locationFilter}
            onChange={e => onLocationChange(e.target.value)}
          >
            <option value="">{t('orders.locationAll')}</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="kanbanTypeFilter">{t('orders.typeFilter')}</label>
          <select
            id="kanbanTypeFilter"
            value={typeFilter}
            onChange={e => onTypeChange(e.target.value)}
          >
            <option value="">{t('orders.typeAll')}</option>
            <option value="DELIVERY">{t('orderType.delivery')}</option>
            <option value="PICKUP">{t('orderType.pickup')}</option>
            <option value="DINE_IN">{t('orderType.dineIn')}</option>
          </select>
        </div>
        <div className="kanban-filters-meta">
          <span className="field-hint">{t('orders.kanban.timezone', { timezone: board.timezone })}</span>
          <button type="button" className="btn btn-ghost" onClick={() => void load()}>
            {t('orders.kanban.refresh')}
          </button>
        </div>
      </div>

      {actionError && <div className="form-error-banner">{actionError}</div>}

      <div className="kanban-board" role="list" aria-label={t('orders.kanban.boardLabel')}>
        {KANBAN_COLUMNS.map(column => {
          const cards = board.columns[column.key] ?? []
          const isOver = dragOverColumn === column.key
          return (
            <section
              key={column.key}
              className={`kanban-column${isOver ? ' kanban-column--over' : ''}`}
              role="listitem"
              onDragOver={event => handleDragOver(event, column.key)}
              onDragLeave={() => {
                setDragOverColumn(current => (current === column.key ? null : current))
                setDropTarget(current => (current && current.column === column.key ? null : current))
              }}
              onDrop={event => handleDrop(event, column.key)}
            >
              <header className="kanban-column-header">
                <span>{t(`orders.kanban.columns.${column.key}`)}</span>
                <span className="kanban-column-count">{cards.length}</span>
              </header>

              <div className="kanban-column-cards">
                {cards.length === 0 ? (
                  <p className="kanban-empty">{t('orders.kanban.emptyColumn')}</p>
                ) : (
                  <>
                {cards.map(card => {
                  const actions = canManage ? availableOrderActions(card.status) : []
                  const isPending = pendingIds.has(card.id)
                  return (
                    <>
                    {dropTarget && dropTarget.column === column.key && dropTarget.beforeId === card.id && (
                      <div className="kanban-drop-indicator" aria-hidden="true" />
                    )}
                    <article
                      key={card.id}
                      data-card-id={card.id}
                      className={`kanban-card${draggingId === card.id ? ' kanban-card--dragging' : ''}${isPending ? ' kanban-card--pending' : ''}`}
                      draggable={canDrag(card)}
                      onDragStart={event => handleDragStart(event, card, column.key)}
                      onDragEnd={clearDragState}
                      onClick={() => navigate(ROUTES.ORDERS_DETAIL.replace(':orderId', card.id))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={event => {
                        if (event.target !== event.currentTarget) return
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          navigate(ROUTES.ORDERS_DETAIL.replace(':orderId', card.id))
                        }
                      }}
                      aria-busy={isPending}
                    >
                      <div className="kanban-card-main">
                        <div className="kanban-card-title-row">
                          <span className="order-number">{card.orderNumber}</span>
                          <OrderStatusBadge status={card.status} />
                        </div>
                        <div className="kanban-card-meta">
                          <span>{t(TYPE_LABEL[card.type] ?? 'orderType.pickup')}</span>
                          {card.location && <span>{card.location.name}</span>}
                        </div>
                        <div className="kanban-card-customer">{card.guestName || '—'}</div>
                        <div className="kanban-card-footer">
                          <span className="kanban-card-time">
                            {column.key === 'completedToday' && card.fulfilledAt
                              ? t('orders.kanban.fulfilledAt', { time: formatDateTime(card.fulfilledAt, board.timezone) })
                              : formatDateTime(card.createdAt, board.timezone)}
                          </span>
                          <span className="kanban-card-total">{card.total} {card.currency}</span>
                        </div>
                      </div>

                      {canManage && actions.length > 0 && (
                        <div className="kanban-card-actions" onClick={event => event.stopPropagation()}>
                          <label className="visually-hidden" htmlFor={`order-status-${card.id}`}>
                            {t('orders.kanban.changeStatus')}
                          </label>
                          <select
                            id={`order-status-${card.id}`}
                            className="kanban-status-select"
                            value=""
                            disabled={isPending}
                            onChange={event => {
                              const next = event.target.value as OrderStatus
                              event.target.value = ''
                              if (next) void changeStatus(card, next)
                            }}
                          >
                            <option value="">{t('orders.kanban.changeStatus')}</option>
                            {actions.map(action => (
                              <option key={action} value={action}>{t(`orderStatus.${action}`)}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </article>
                    </>
                  )
                })}
                {dropTarget && dropTarget.column === column.key && dropTarget.beforeId === null && (
                  <div className="kanban-drop-indicator" aria-hidden="true" />
                )}
                  </>
                )}
              </div>
            </section>
          )
        })}
      </div>

      <p className="kanban-hint">{t('orders.kanban.cancelledHint')}</p>
    </div>
  )
}
